import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from './api';
import LoadingSpinner from './components/LoadingSpinner';

export default function Leaderboard() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/leaderboard`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch leaderboard');
        }
        
        const result = await response.json();
        setLeaderboard(result.leaderboard || []);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchLeaderboard();
    }
  }, [isAuthenticated, authenticatedFetch]);

  const getDisplayName = (member) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    if (member.firstName) {
      return member.firstName;
    }
    if (member.lastName) {
      return member.lastName;
    }
    if (member.name) {
      return member.name;
    }
    return member.email || 'Unknown Member';
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  if (authLoading || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh'
      }}>
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={{ 
      maxWidth: '900px', 
      margin: '0 auto', 
      padding: '2rem 1.5rem',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: '700', 
          color: '#333', 
          marginBottom: '0.5rem'
        }}>
          Leaderboard
        </h1>
        <p style={{ 
          fontSize: '1.1rem', 
          color: '#666',
          marginBottom: '1rem'
        }}>
          Top community members ranked by points
        </p>
      </div>

      {/* Leaderboard Table */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px'
        }}>
          <LoadingSpinner message="Loading leaderboard..." />
        </div>
      ) : leaderboard.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            No users with points yet. Be the first to earn points!
          </p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{ 
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6'
              }}>
                <th style={{ 
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.9rem',
                  width: '80px'
                }}>
                  Rank
                </th>
                <th style={{ 
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  User
                </th>
                <th style={{ 
                  padding: '1rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.9rem',
                  width: '120px'
                }}>
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((member) => (
                <tr 
                  key={member.id}
                  style={{ 
                    borderBottom: '1px solid #e9ecef',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem'
                    }}>
                      {getRankEmoji(member.rank) && (
                        <span style={{ fontSize: '1.5rem' }}>
                          {getRankEmoji(member.rank)}
                        </span>
                      )}
                      <span style={{ 
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: member.rank <= 3 ? '#38b44a' : '#666'
                      }}>
                        #{member.rank}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <Link 
                      to={`/profile/${member.id}`}
                      style={{
                        color: '#38b44a',
                        textDecoration: 'none',
                        fontWeight: '500',
                        fontSize: '1rem'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {getDisplayName(member)}
                    </Link>
                  </td>
                  <td style={{ 
                    padding: '1rem',
                    textAlign: 'right'
                  }}>
                    <span style={{ 
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {member.points.toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
